/**
 * API Caching Composable
 * Provides client-side caching with TTL and request deduplication
 *
 * Features:
 * - In-memory cache with TTL
 * - Request deduplication (prevent duplicate API calls)
 * - Automatic cache invalidation
 * - Type-safe
 *
 * Usage:
 * ```typescript
 * // Basic usage
 * const { data, loading, error } = useApiCache('/campaigns/active', 300000) // 5 min cache
 *
 * // With manual refresh
 * const api = useApiCache('/users/active', 60000)
 * await api.refresh() // Force refresh
 *
 * // Invalidate cache
 * invalidateCache('/campaigns/active')
 * ```
 */

import type { Ref } from 'vue'
import { ref, watch } from 'vue'
import { useApi } from './useApi'

// Logger will be auto-imported by vite config
// import Logger from '@/utils/logger'
const Logger = console // Fallback for now

interface CacheEntry<T> {
  data: T
  timestamp: number
  promise?: Promise<any>
}

// Global cache storage
const cache = new Map<string, CacheEntry<any>>()

// Pending requests (for deduplication)
const pendingRequests = new Map<string, Promise<any>>()

// ✅ LRU Cache Configuration
const MAX_CACHE_SIZE = 100

/**
 * Evict oldest cache entry (LRU - Least Recently Used)
 */
function evictOldestCache(): void {
  if (cache.size <= MAX_CACHE_SIZE)
    return

  // Find oldest entry by timestamp
  let oldestKey: string | null = null
  let oldestTime = Number.POSITIVE_INFINITY

  for (const [key, entry] of cache.entries()) {
    if (entry.timestamp < oldestTime) {
      oldestTime = entry.timestamp
      oldestKey = key
    }
  }

  if (oldestKey) {
    cache.delete(oldestKey)
    Logger.debug(`🗑️ LRU evicted: ${oldestKey}`)
  }
}

/**
 * Check if cache is still valid
 */
function isCacheValid(entry: CacheEntry<any>, ttl: number): boolean {
  return Date.now() - entry.timestamp < ttl
}

/**
 * Invalidate cache by key
 */
export function invalidateCache(key: string): void {
  cache.delete(key)
  Logger.debug(`🗑️ Cache invalidated: ${key}`)
}

/**
 * Invalidate cache by pattern
 */
export function invalidateCachePattern(pattern: string): void {
  let count = 0

  for (const key of cache.keys()) {
    if (key.includes(pattern)) {
      cache.delete(key)
      count++
    }
  }
  Logger.debug(`🗑️ Cache invalidated (${count} keys): *${pattern}*`)
}

/**
 * Clear all cache
 */
export function clearAllCache(): void {
  const count = cache.size

  cache.clear()
  pendingRequests.clear()
  Logger.info(`🗑️ All cache cleared (${count} entries)`)
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  return {
    entries: cache.size,
    pendingRequests: pendingRequests.size,
    keys: Array.from(cache.keys()),
  }
}

/**
 * API Cache Composable
 */
export function useApiCache<T = any>(
  url: string | Ref<string>,
  ttl: number = 300000, // 5 minutes default
  options: {
    immediate?: boolean
    refetch?: boolean
  } = {},
) {
  const {
    immediate = true,
    refetch = false,
  } = options

  const data = ref<T | null>(null) as Ref<T | null>
  const loading = ref(false)
  const error = ref<Error | null>(null)

  /**
   * Fetch data (with caching and deduplication)
   */
  async function fetchData(forceRefresh = false) {
    const key = typeof url === 'string' ? url : url.value

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cache.get(key)

      if (cached && isCacheValid(cached, ttl)) {
        Logger.debug(`✅ Cache HIT: ${key}`)
        data.value = cached.data
        loading.value = false

        return cached.data
      }
    }

    // Check if same request is already in flight (request deduplication)
    if (pendingRequests.has(key)) {
      Logger.debug(`⏳ Request deduplication: ${key}`)
      loading.value = true

      const result = await pendingRequests.get(key)

      data.value = result
      loading.value = false

      return result
    }

    // Make new request
    Logger.debug(`❌ Cache MISS: ${key}`)
    loading.value = true
    error.value = null

    try {
      // Create promise and store it for deduplication
      const apiCall = useApi(key).get().json<T>()

      // Store as Promise for deduplication (type cast needed)
      pendingRequests.set(key, apiCall as any)

      const response = await apiCall

      if (response.error.value)
        throw new Error(response.error.value.message || 'API Error')

      const result = response.data.value

      // ✅ Evict old cache if needed before adding new entry
      evictOldestCache()

      // Store in cache
      cache.set(key, {
        data: result,
        timestamp: Date.now(),
      })

      data.value = result
      loading.value = false

      // Remove from pending
      pendingRequests.delete(key)

      return result
    }
    catch (err: any) {
      error.value = err
      loading.value = false
      pendingRequests.delete(key)
      throw err
    }
  }

  /**
   * Refresh data (bypass cache)
   */
  async function refresh() {
    return fetchData(true)
  }

  /**
   * Invalidate this cache entry
   */
  function invalidate() {
    const key = typeof url === 'string' ? url : url.value

    invalidateCache(key)
  }

  // Watch URL changes (if reactive)
  if (typeof url !== 'string') {
    watch(url, () => {
      if (refetch)
        fetchData()
    })
  }

  // Initial fetch
  if (immediate)
    fetchData()

  return {
    data,
    loading,
    error,
    refresh,
    invalidate,
    execute: fetchData, // Alias for manual execution
  }
}

/**
 * Predefined cache keys for common endpoints
 */
export const CACHE_KEYS = {
  // Campaigns
  CAMPAIGNS_ACTIVE: '/campaigns/active',
  CAMPAIGN_DETAIL: (id: number) => `/campaigns/${id}`,

  // Wrapup
  WRAPUP_GROUP_STATUSES: '/wrapup-group-status/active',
  WRAPUP_TEMPLATES: '/wrapup-template/active',
  WRAPUP_TREE: (groupStatusId: number) => `/wrapup?groupStatusId=${groupStatusId}`,

  // Teams
  TEAMS_ACTIVE: '/teams/active',

  // Users
  USERS_ACTIVE: '/users/active',

  // External Apps
  EXTERNAL_APPS_ACTIVE: '/external-apps/active',

  // Call Lists
  CALL_LISTS_ACTIVE: '/call-lists/active',

  // DNC Groups
  DNC_GROUPS_ACTIVE: '/dnc/groups/active',
}

/**
 * Predefined TTLs
 */
export const CACHE_TTL = {
  VERY_SHORT: 60 * 1000, // 1 minute
  SHORT: 5 * 60 * 1000, // 5 minutes
  MEDIUM: 10 * 60 * 1000, // 10 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
  VERY_LONG: 60 * 60 * 1000, // 1 hour
}
